import { subtract } from 'lodash';
import { inject, injectable } from 'tsyringe';
import { BaseApi } from '../BaseApi';
import { MutualFundUnit, OrderMarketTime, OrderTransaction } from '../constants/lookups';
import { ApiInjectToken, MiddlewareInjectToken } from '../constants/tokens';
import {
    CancelledCryptoOrderInfo,
    CancelModifyOrder,
    CryptoOrderHistoryWithExtraInfo,
    CryptoTradePreview,
    MiddlewareConfig,
    Model,
    ModifyOrderFormValues,
    Order,
    OrderWithExtraInfo,
    Sdks,
} from '../types';
import { CryptoTradingFormValues, OrderPreview, TradingFormMeta, TradingFormValues } from '../types/Trading.type';
import { formatOrders } from '../utils';
import { IUserApi } from './interfaces';
import { IQuoteApi } from './interfaces/IQuoteApi';
import { ITradingApi } from './interfaces/ITradingApi';

@injectable()
export class TradingApi extends BaseApi implements ITradingApi {
    constructor(
        @inject(MiddlewareInjectToken.Sdks) sdks: Sdks,
        @inject(MiddlewareInjectToken.Config) config: MiddlewareConfig,
        @inject(ApiInjectToken.UserApi) protected userApi: IUserApi,
        @inject(ApiInjectToken.QuoteApi) protected quoteApi: IQuoteApi,
    ) {
        super(sdks, config);
    }
    protected prepareOrder(values: TradingFormValues, meta: TradingFormMeta) {
        const {
            symbol,
            orderType,
            limitPrice,
            stopPrice,
            transaction,
            fillType,
            quantity,
            contracts,
            marketTime,
            validity,
            optionLegs,
            mutualFundUnit,
            mutualFundValue,
            notionalValue,
            accountId,
            modelId,
        } = values;

        const {
            modelTrading,
            inventoryTrading,
            optionTrading,
            spreadTrading,
            mutualFundTrading,
            notionalOrder,
            limitOrder,
            stopOrder,
        } = meta;

        let orderBody: any;

        if (mutualFundTrading) {
            const buying = transaction === OrderTransaction.Buy;

            orderBody = {
                account: {
                    accountId,
                },
                requireNbbo: true,
                order: {
                    quote: {
                        symbol,
                    },
                    transaction,
                    value:
                        !buying && mutualFundUnit === MutualFundUnit.Dollar
                            ? mutualFundValue
                            : buying
                            ? mutualFundValue
                            : mutualFundUnit === MutualFundUnit.Dollar
                            ? mutualFundValue
                            : undefined,
                    quantity: !buying && mutualFundUnit === MutualFundUnit.Unit ? mutualFundValue : undefined,
                },
            };
        } else if (spreadTrading) {
            const open = transaction === OrderTransaction.Open;

            orderBody = {
                account: {
                    accountId,
                },
                requireNbbo: true,
                order: {
                    validity,
                    orderType,
                    spreadPrice: subtract(optionLegs?.[0]?.askSize || 0, optionLegs?.[1]?.bidSize || 0),
                    legs: [
                        {
                            quantity: contracts,
                            quote: {
                                symbol: optionLegs?.[0].symbol,
                                option: true,
                            },
                            transaction: open ? OrderTransaction.BuyToOpen : OrderTransaction.BuyToClose,
                        },
                        {
                            quantity: contracts,
                            quote: {
                                symbol: optionLegs?.[1].symbol,
                                option: true,
                            },
                            transaction: open ? OrderTransaction.SellToOpen : OrderTransaction.SellToClose,
                        },
                    ],
                },
            };
        } else {
            orderBody = {
                requireNbbo: true,
                order: {
                    quote: {
                        symbol: optionTrading ? optionLegs?.[0].symbol : symbol,
                        option: optionTrading,
                    },
                    marketTime: optionTrading ? OrderMarketTime.CoreMarket : marketTime,
                    quantity: notionalOrder ? undefined : optionTrading ? contracts : quantity,
                    expectedValue: notionalOrder ? notionalValue : undefined,
                    transaction,
                    orderType,
                    /// Short is not supported / not needed by API when short selling, but this acts as selection placeholder
                    // removing positionType as per Daniil (06.03.22)
                    // orderAccountType: positionType === PositionType.Short ? '' : positionType,
                    fillType,
                    validity,
                },
            };

            if (limitOrder) {
                orderBody.order.limitPrice = limitPrice;
            }

            if (stopOrder) {
                orderBody.order.stopPrice = stopPrice;
            }

            if (modelTrading) {
                orderBody.model = {
                    id: modelId,
                };
            } else if (inventoryTrading) {
                orderBody.inventory = true;
            } else {
                orderBody.account = {
                    accountId,
                };
            }
        }

        return orderBody;
    }

    private prepareCryptoOrder(values: CryptoTradingFormValues, accountNumber: string) {
        const { coin, orderType, quantity, limitPrice, transaction } = values;
        return {
            account: {
                accountNumber,
            },
            order: {
                orderType,
                quote: {
                    symbol: coin,
                },
                quantity,
                limitPx: limitPrice,
                transaction: transaction.toUpperCase(),
            },
        };
    }

    async previewOrder(values: TradingFormValues, meta: TradingFormMeta): Promise<OrderPreview | null> {
        const orderBody = this.prepareOrder(values, meta);
        const { modelTrading, inventoryTrading, mutualFundTrading, optionTrading, spreadTrading } = meta;

        const { data } = await (mutualFundTrading
            ? this.sdks.orbis.post('orders/v2/mf/preview', orderBody)
            : optionTrading
            ? spreadTrading
                ? this.sdks.orbis.post('orders/v2/preview/options/multileg', orderBody)
                : this.sdks.orbis.post('orders/v2/preview/option', orderBody)
            : modelTrading || inventoryTrading
            ? this.sdks.orbis.post('orders/v2/advisory/equity/preview', orderBody)
            : this.sdks.orbis.post('orders/v2/preview/equity', orderBody));

        return data;
    }

    async placeOrder(values: TradingFormValues, meta: TradingFormMeta): Promise<string> {
        const orderBody = this.prepareOrder(values, meta);
        const { modelTrading, inventoryTrading, mutualFundTrading, optionTrading, spreadTrading } = meta;

        const { data } = await (mutualFundTrading
            ? this.sdks.orbis.post('orders/v2/mf/place', orderBody)
            : optionTrading
            ? spreadTrading
                ? this.sdks.orbis.post('orders/v2/place/options/multileg', orderBody)
                : this.sdks.orbis.post('orders/v2/place/option', orderBody)
            : modelTrading || inventoryTrading
            ? this.sdks.orbis.post('orders/v2/advisory/equity/place', orderBody)
            : this.sdks.orbis.post('orders/v2/place/equity', orderBody));

        return data.orderRef || data.OrderRef;
    }

    async modifyOrder(values: ModifyOrderFormValues): Promise<string> {
        const response = await this.sdks.orbis.post<CancelModifyOrder>('orders/v2/replace', { order: values });
        return response.data?.orderRef || response.data?.OrderRef || '';
    }

    async cancelOrder(orderRef: string): Promise<string> {
        const response = await this.sdks.orbis.post<CancelModifyOrder>('orders/v2/cancel', { order: { orderRef } });
        return response.data?.orderRef || response.data?.OrderRef || '';
    }

    async previewCryptoOrder(values: CryptoTradingFormValues, accountNumber: string): Promise<CryptoTradePreview> {
        const response = await this.sdks.orbis.post(
            'orders/v2/crypto/preview',
            this.prepareCryptoOrder(values, accountNumber),
        );
        return response.data;
    }

    async placeCryptoOrder(values: CryptoTradingFormValues, accountNumber: string): Promise<OrderWithExtraInfo> {
        const response = await this.sdks.orbis.post(
            'orders/v2/crypto/place',
            this.prepareCryptoOrder(values, accountNumber),
        );
        return response.data;
    }

    async getOrderDetails(orderRef: string): Promise<OrderWithExtraInfo | null> {
        const data = await Promise.all([
            this.config.apiPermissions.advisor
                ? this.sdks.orbis.get('v2/advisory/models')
                : Promise.resolve({ data: [] }),
            this.userApi.getUserInfo(),
            this.sdks.orbis.get('orders/status', undefined, {
                urlQuery: orderRef,
                params: { loadQuotes: true },
            }),
        ]);

        const models = data[0].data as Model[];
        const accounts = data[1].accountList;
        const order = data[2].data as Order | null;

        if (!order) {
            return null;
        }

        return formatOrders([order], models, accounts)[0];
    }

    /* eslint-disable @typescript-eslint/no-unused-vars */

    async getCryptoOrderDetails(accountNumber: string, orderRef: string): Promise<OrderWithExtraInfo | null> {
        return this.getOrderDetails(orderRef);
    }

    getCryptoOrderStatus(accountId: string, requestId: string): Promise<CryptoOrderHistoryWithExtraInfo> {
        throw new Error('Not supported');
    }

    cancelCryptoOrder(accountNumber: string, orderId: string): Promise<CancelledCryptoOrderInfo> {
        throw new Error('Not supported');
    }

    /* eslint-enable @typescript-eslint/no-unused-vars */
}
